import { createContext, useContext, useEffect, useState, useCallback } from "react";

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "gx_mobile_token";

const MobileAuthContext = createContext(null);

export function formatError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function MobileAuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(!!localStorage.getItem(TOKEN_KEY));

  const authFetch = useCallback(
    async (path, options = {}) => {
      const headers = { "Content-Type": "application/json", ...(options.headers || {}) };
      const t = localStorage.getItem(TOKEN_KEY);
      if (t) headers.Authorization = `Bearer ${t}`;
      const res = await fetch(`${API}${path}`, { ...options, headers });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(formatError(data.detail));
      return data;
    },
    []
  );

  const refresh = useCallback(async () => {
    const t = localStorage.getItem(TOKEN_KEY);
    if (!t) {
      setProfile(null);
      setLoading(false);
      return;
    }
    try {
      const data = await authFetch("/api/mobile/auth/me");
      setProfile(data.profile);
    } catch (e) {
      localStorage.removeItem(TOKEN_KEY);
      setToken(null);
      setProfile(null);
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    const res = await fetch(`${API}/api/mobile/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(formatError(data.detail));
    localStorage.setItem(TOKEN_KEY, data.token);
    setToken(data.token);
    setProfile(data.profile);
    return data;
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setProfile(null);
  };

  return (
    <MobileAuthContext.Provider
      value={{ token, profile, setProfile, loading, isAuthed: !!token, login, logout, refresh, authFetch }}
    >
      {children}
    </MobileAuthContext.Provider>
  );
}

export function useMobileAuth() {
  const ctx = useContext(MobileAuthContext);
  if (!ctx) throw new Error("useMobileAuth must be used within MobileAuthProvider");
  return ctx;
}
